import {
  ChangeDetectorRef,
  Injectable,
  OnDestroy,
  inject,
} from '@angular/core';

const SIDEBAR_PINNED_KEY = 'tc_layout_sidebar_pinned';

/** Sidebar pin, peek, and mobile drawer state for `tc-layout`. */
@Injectable()
export class SidebarStateService implements OnDestroy {
  private readonly cdr = inject(ChangeDetectorRef);

  isSidebarPinned = true;
  isSidebarPeekOpen = false;
  isSidebarMobileOpen = false;
  sidebarTriggerBlink = false;
  sidebarTriggerBlinkUnlock = false;

  /** Matches header `h-14` — peek must not steal the hamburger hit target. */
  private static readonly HEADER_HEIGHT_PX = 56;
  /** Left-edge hover strip width — keep in sync with `--sidebar-peek-edge`. */
  private static readonly PEEK_EDGE_WIDTH_PX = 80;
  private static readonly PEEK_CLOSE_DELAY_MS = 80;
  private static readonly PIN_BLINK_MS = 700;

  private peekCloseTimer: ReturnType<typeof setTimeout> | null = null;
  private pinBlinkTimer: ReturnType<typeof setTimeout> | null = null;

  init(): void {
    this.loadSidebarPinnedPreference();
    if (typeof document !== 'undefined') {
      document.addEventListener('mousemove', this.onDocumentMouseMovePeek);
    }
    this.cdr.markForCheck();
  }

  ngOnDestroy(): void {
    this.cancelPeekClose();
    this.cancelPinBlink();
    if (typeof document !== 'undefined') {
      document.removeEventListener('mousemove', this.onDocumentMouseMovePeek);
    }
  }

  get isSidebarPanelOpen(): boolean {
    if (this.isMobileViewport()) {
      return this.isSidebarMobileOpen;
    }
    return this.isSidebarPinned || this.isSidebarPeekOpen;
  }

  get isSidebarDockedOpen(): boolean {
    return !this.isMobileViewport() && this.isSidebarPanelOpen;
  }

  /** Hover-to-open only when sidebar is unpinned (collapsed state). */
  get enableSidebarPeek(): boolean {
    return !this.isMobileViewport() && !this.isSidebarPinned;
  }

  isMobileViewport(): boolean {
    return typeof window !== 'undefined' && window.innerWidth < 768;
  }

  closeMobileSidebar(): void {
    this.isSidebarMobileOpen = false;
    this.cdr.markForCheck();
  }

  toggleSidebar(): void {
    if (this.isMobileViewport()) {
      this.isSidebarMobileOpen = !this.isSidebarMobileOpen;
      this.cdr.markForCheck();
      return;
    }

    this.cancelPeekClose();
    const unlocking = this.isSidebarPinned;
    this.isSidebarPinned = !this.isSidebarPinned;
    this.isSidebarPeekOpen = false;
    this.persistPinned(this.isSidebarPinned);
    this.flashPinBlink(unlocking);
    this.cdr.markForCheck();
  }

  onWindowResize(): void {
    if (this.isMobileViewport()) {
      this.cancelPeekClose();
      this.isSidebarPeekOpen = false;
      this.cdr.markForCheck();
    }
  }

  onPeekEdgeEnter(): void {
    if (!this.enableSidebarPeek) {
      return;
    }
    this.cancelPeekClose();
    this.setPeekOpen(true);
  }

  onPeekEdgeLeave(event: MouseEvent): void {
    if (!this.enableSidebarPeek || !this.isSidebarPeekOpen) {
      return;
    }
    if (this.isWithinPeekUi(event.relatedTarget)) {
      return;
    }
    this.schedulePeekClose();
  }

  onPeekSidebarEnter(): void {
    if (!this.enableSidebarPeek) {
      return;
    }
    this.cancelPeekClose();
    this.setPeekOpen(true);
  }

  onPeekSidebarLeave(event: MouseEvent): void {
    if (!this.enableSidebarPeek || !this.isSidebarPeekOpen) {
      return;
    }
    if (this.isWithinPeekUi(event.relatedTarget)) {
      return;
    }
    this.schedulePeekClose();
  }

  private flashPinBlink(unlocking: boolean): void {
    if (this.pinBlinkTimer !== null) {
      clearTimeout(this.pinBlinkTimer);
      this.pinBlinkTimer = null;
    }

    this.sidebarTriggerBlinkUnlock = unlocking;
    this.sidebarTriggerBlink = false;
    this.cdr.detectChanges();

    requestAnimationFrame(() => {
      this.sidebarTriggerBlink = true;
      this.cdr.markForCheck();
      this.pinBlinkTimer = setTimeout(() => {
        this.pinBlinkTimer = null;
        this.sidebarTriggerBlink = false;
        this.cdr.markForCheck();
      }, SidebarStateService.PIN_BLINK_MS);
    });
  }

  private cancelPinBlink(): void {
    if (this.pinBlinkTimer !== null) {
      clearTimeout(this.pinBlinkTimer);
      this.pinBlinkTimer = null;
    }
    this.sidebarTriggerBlink = false;
    this.sidebarTriggerBlinkUnlock = false;
  }

  private readonly onDocumentMouseMovePeek = (event: MouseEvent): void => {
    if (!this.enableSidebarPeek) {
      return;
    }

    const x = event.clientX;
    const y = event.clientY;

    if (
      !this.isSidebarPeekOpen &&
      y >= SidebarStateService.HEADER_HEIGHT_PX &&
      x <= SidebarStateService.PEEK_EDGE_WIDTH_PX
    ) {
      this.cancelPeekClose();
      this.setPeekOpen(true);
      return;
    }

    if (!this.isSidebarPeekOpen) {
      return;
    }

    if (this.isInPeekKeepOpenZone(x, y)) {
      this.cancelPeekClose();
    } else {
      this.closePeekNow();
    }
  };

  private isInPeekKeepOpenZone(x: number, y: number): boolean {
    const sidebarWidth = this.getSidebarWidthPx();

    if (x <= sidebarWidth) {
      return true;
    }

    if (
      y < SidebarStateService.HEADER_HEIGHT_PX &&
      x <= sidebarWidth + this.getPinCorridorPx()
    ) {
      return true;
    }

    if (
      y >= SidebarStateService.HEADER_HEIGHT_PX &&
      x <= SidebarStateService.PEEK_EDGE_WIDTH_PX
    ) {
      return true;
    }

    return false;
  }

  private getPinCorridorPx(): number {
    if (typeof document === 'undefined') {
      return 64;
    }
    const raw = getComputedStyle(document.documentElement)
      .getPropertyValue('--sidebar-pin-corridor')
      .trim();
    if (!raw) {
      return 64;
    }
    const rootFontSize =
      parseFloat(getComputedStyle(document.documentElement).fontSize) || 16;
    if (raw.endsWith('rem')) {
      return parseFloat(raw) * rootFontSize;
    }
    if (raw.endsWith('px')) {
      return parseFloat(raw);
    }
    return parseFloat(raw) || 64;
  }

  private isWithinPeekUi(target: EventTarget | null): boolean {
    if (!(target instanceof Element)) {
      return false;
    }
    return !!target.closest(
      '[data-tc-sidebar], .tc-sidebar-edge, [data-tc-sidebar-trigger]',
    );
  }

  private schedulePeekClose(): void {
    if (this.peekCloseTimer !== null) {
      return;
    }
    this.peekCloseTimer = setTimeout(() => {
      this.peekCloseTimer = null;
      this.setPeekOpen(false);
    }, SidebarStateService.PEEK_CLOSE_DELAY_MS);
  }

  private closePeekNow(): void {
    this.cancelPeekClose();
    this.setPeekOpen(false);
  }

  private cancelPeekClose(): void {
    if (this.peekCloseTimer === null) {
      return;
    }
    clearTimeout(this.peekCloseTimer);
    this.peekCloseTimer = null;
  }

  private setPeekOpen(open: boolean): void {
    if (this.isSidebarPinned || this.isSidebarPeekOpen === open) {
      return;
    }
    this.isSidebarPeekOpen = open;
    this.cdr.markForCheck();
  }

  private getSidebarWidthPx(): number {
    if (typeof document === 'undefined') {
      return 256;
    }
    const raw = getComputedStyle(document.documentElement)
      .getPropertyValue('--sidebar-width')
      .trim();
    if (!raw) {
      return 256;
    }
    const rootFontSize =
      parseFloat(getComputedStyle(document.documentElement).fontSize) || 16;
    if (raw.endsWith('rem')) {
      return parseFloat(raw) * rootFontSize;
    }
    if (raw.endsWith('px')) {
      return parseFloat(raw);
    }
    return parseFloat(raw) || 256;
  }

  private loadSidebarPinnedPreference(): void {
    try {
      const stored = localStorage.getItem(SIDEBAR_PINNED_KEY);
      // Default open when unset; '0' / '1' remember an explicit user choice.
      this.isSidebarPinned = stored !== '0';
    } catch {
      this.isSidebarPinned = true;
    }
  }

  private persistPinned(pinned: boolean): void {
    try {
      localStorage.setItem(SIDEBAR_PINNED_KEY, pinned ? '1' : '0');
    } catch {
      /* ignore */
    }
  }
}
